import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IconAlertTriangle } from '@/components/icons/alert-triangle';
import { IconCircleCheck } from '@/components/icons/circle-check';
import { hasNativeBridge } from '@/lib/capacitor';
import { subscribeToConnectivity } from '@/lib/network-status';
import Sentry from '@/lib/sentry';

const RESTORED_DURATION = 3_000;

export function NetworkBanner() {
  const { t } = useTranslation();
  const [state, setState] = useState(null);
  const wasOffline = useRef(false);

  useEffect(() => {
    let active = true;
    let timeout;
    const update = (connected) => {
      if (!active) return;

      window.clearTimeout(timeout);
      if (!connected) {
        wasOffline.current = true;
        setState('offline');
      } else if (wasOffline.current) {
        wasOffline.current = false;
        setState('online');
        timeout = window.setTimeout(() => setState(null), RESTORED_DURATION);
      }
    };
    const unsubscribe = subscribeToConnectivity({
      isNative: hasNativeBridge(),
      onChange: update,
      onError: (error) => Sentry.captureException(error),
    });

    return () => {
      active = false;
      window.clearTimeout(timeout);
      unsubscribe();
    };
  }, []);

  if (!state) return null;

  const offline = state === 'offline';
  const Icon = offline ? IconAlertTriangle : IconCircleCheck;

  return (
    <div
      role="status"
      aria-live="polite"
      className={`native-network-banner fixed inset-x-0 top-0 z-[100] flex min-h-11 items-center justify-center gap-2 px-4 py-2 text-center text-sm font-medium shadow-md ${
        offline
          ? 'bg-destructive text-destructive-foreground'
          : 'bg-status-optimal text-primary-foreground'
      }`}
    >
      <Icon className="size-4 shrink-0" aria-hidden="true" />
      <span>{t(`network.${state}`)}</span>
    </div>
  );
}
